import { Environment } from '../../environment.js'
import type { Workspace, WorkspaceSummary } from '../../Flux/Slice/Chat/Workspace.js'
import { migrateWorkspace } from '../../Flux/Slice/Chat/Workspace.js'
import type { SaveWorkspaceRequest } from '../../Generated/SaveWorkspaceRequest.js'
import type { WorkspaceBlobDetail } from '../../Generated/WorkspaceBlobDetail.js'
import type { WorkspaceBlobSummary } from '../../Generated/WorkspaceBlobSummary.js'
import sentryTraceHeaders from '../Sentry.js'
import type { PayloadCodec } from './PayloadCodec.js'
import { NoopJsonCodec } from './PayloadCodec.js'
import type { WorkspaceStore } from './WorkspaceStore.js'

/**
 * W-3b — the SERVER-SIDE {@link WorkspaceStore}: a drop-in for {@link IndexedDbWorkspaceStore} that syncs
 * workspaces to the user's account instead of this browser. It POSTs/GETs the SAME opaque codec blob the
 * browser store persists — so this is a store swap, not a call-site change — and the BACKEND encrypts that
 * blob at rest via its CryptoService (KMS envelope encryption). Server-key-at-rest, NOT client-E2E: the
 * frontend {@link PayloadCodec} stays the no-op JSON codec, so the blob leaves the browser as plaintext JSON
 * and the server is the encryption boundary.
 *
 * The backend endpoints (`/api/chat/workspace*`) only exist when server-side crypto is enabled; when it is
 * off they 404, every method here rejects, and the opt-in control in `WorkspaceControls` degrades to the
 * browser-only store (see its fallback handling).
 *
 * Cookie-authenticated like the rest of the chat API (no bearer). OWNER-ONLY on the server: `list` never
 * returns another user's workspaces, and `get`/`delete` of a non-owned id 404.
 */
export class ServerWorkspaceStore implements WorkspaceStore {
  constructor(private readonly codec: PayloadCodec = NoopJsonCodec) {}

  private base(): string {
    return `${Environment.API_URL}/api/chat`
  }

  private headers(json: boolean): Record<string, string> {
    return { ...(json ? { 'Content-Type': 'application/json' } : {}), ...sentryTraceHeaders() }
  }

  async save(ws: Workspace): Promise<void> {
    const body: SaveWorkspaceRequest = {
      id: ws.id,
      title: ws.title,
      scopeKey: ws.scopeKey,
      // The frontend codec is a no-op → this is plaintext JSON; the server encrypts it at rest.
      blob: await this.codec.encode(ws)
    }
    const res = await fetch(`${this.base()}/workspace`, {
      method: 'POST',
      credentials: 'include',
      headers: this.headers(true),
      body: JSON.stringify(body)
    })
    if (!res.ok) throw new Error(`Could not save workspace (${res.status})`)
  }

  async list(scopeKey: string): Promise<WorkspaceSummary[]> {
    const params = new URLSearchParams({ scopeKey })
    const res = await fetch(`${this.base()}/workspaces?${params.toString()}`, {
      credentials: 'include',
      headers: this.headers(false)
    })
    if (!res.ok) throw new Error(`Could not list workspaces (${res.status})`)
    const rows = (await res.json()) as WorkspaceBlobSummary[]
    // The server already returns newest-first; map its `updatedAt` onto the store-facing `savedAt`.
    return rows.map((r) => ({ id: r.id, title: r.title, scopeKey: r.scopeKey, savedAt: r.updatedAt ?? '' }))
  }

  async get(id: string): Promise<Workspace | null> {
    const res = await fetch(`${this.base()}/workspace/${encodeURIComponent(id)}`, {
      credentials: 'include',
      headers: this.headers(false)
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`Could not load workspace (${res.status})`)
    const detail = (await res.json()) as WorkspaceBlobDetail
    return migrateWorkspace((await this.codec.decode(detail.blob)) as Workspace)
  }

  async delete(id: string): Promise<void> {
    const res = await fetch(`${this.base()}/workspace/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: this.headers(false)
    })
    if (!res.ok && res.status !== 404) throw new Error(`Could not delete workspace (${res.status})`)
  }
}
